import { useState, useEffect } from 'react';
import { Menu, X, Linkedin, Globe, ChevronDown, Calendar, Building2, Cpu, Users, Newspaper, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/i18n/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/c3a67mbc_0g%20Baltics_SSb-R00a_Mil.png";

const languages = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'lv', label: 'LV', name: 'Latvian' },
  { code: 'lt', label: 'LT', name: 'Lithuanian' },
  { code: 'ee', label: 'EE', name: 'Estonian' },
];

export const Header = ({ alwaysVisible = false }) => {
  const { t, language, changeLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(alwaysVisible);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Grouped navigation structure
  const solutionsMenu = [
    { name: t('nav.realEstate'), href: '/real-estate' },
    { name: 'E2 Insurance', href: '/e2-insurance' },
    { name: t('nav.smartMetering'), href: '/smart-metering' },
    { name: t('nav.assetTracking'), href: '/asset-tracking' },
    { name: t('nav.toolTracking'), href: '/tool-tracking' },
    { name: t('nav.indoorAirQuality'), href: '/indoor-air-quality-monitoring' },
    { name: t('nav.smartLockers'), href: '/smart-lockers' },
    { name: t('nav.buildingEnergy'), href: '/building-energy-efficiency' },
    { name: t('nav.officeSpace'), href: '/office-space-management' },
    { name: t('nav.smartCities'), href: '/smart-city-iot-infrastructure' },
  ];

  const productsMenu = [
    { name: t('nav.tektelic'), href: '/tektelic', desc: t('nav.tektelicDesc') },
    { name: t('nav.connectivity'), href: '/connectivity', desc: t('nav.connectivityDesc') },
  ];

  const companyMenu = [
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.partners'), href: '#partners' },
    { name: t('nav.industries'), href: '#industries' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  useEffect(() => {
    if (alwaysVisible) {
      setIsVisible(true);
      return;
    }
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when scrolled down past 100px
      if (currentScrollY > 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, alwaysVisible]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="container-0g">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="0g Baltics" 
              className="h-10 md:h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Solutions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="header-solutions-dropdown"
                >
                  <Building2 className="h-4 w-4" />
                  {t('nav.solutionsMenu')}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 max-h-none">
                <DropdownMenuLabel className="text-xs text-slate-500 uppercase">{t('nav.iotSolutions')}</DropdownMenuLabel>
                {solutionsMenu.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link to={item.href} className="cursor-pointer">
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="header-products-dropdown"
                >
                  <Cpu className="h-4 w-4" />
                  {t('nav.products')}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500 uppercase">{t('nav.hardwareInfra')}</DropdownMenuLabel>
                {productsMenu.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link to={item.href} className="cursor-pointer flex flex-col items-start">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-xs text-slate-500">{item.desc}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Company Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="header-company-dropdown"
                >
                  <Users className="h-4 w-4" />
                  {t('nav.company')}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {companyMenu.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <a href={item.href} className="cursor-pointer">
                      {item.name}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Blog Link */}
            <Link 
              to="/blog"
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="header-blog-link"
            >
              <Newspaper className="h-4 w-4" />
              {t('nav.blog')}
            </Link>

            {/* E2 Insurance Link */}
            <Link
              to="/e2-insurance"
              className="relative flex items-center gap-1.5 ml-1 px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm hover:shadow-md transition-all"
              data-testid="header-e2-insurance-link"
            >
              <Droplets className="h-4 w-4 text-teal-400" />
              E2 Insurance
              <span className="absolute -top-2 -right-2 flex h-5 items-center px-1.5 rounded-full bg-teal-400 text-[10px] font-bold text-slate-900 shadow ring-2 ring-white">
                {t('nav.balticHubBadge')}
              </span>
            </Link>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="hidden md:flex items-center space-x-1 text-slate-600"
                  data-testid="language-switcher"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">{language.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={language === lang.code ? 'bg-teal-50 text-teal-600' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/0gbaltics"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex text-slate-600 hover:text-teal-600 transition-colors"
              data-testid="linkedin-link-header"
            >
              <Linkedin className="h-5 w-5" />
            </a>

            {/* Schedule Demo Button */}
            <Button 
              className="hidden sm:inline-flex btn-primary items-center gap-2"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="header-schedule-demo"
            >
              <Calendar className="h-4 w-4" />
              {t('nav.scheduleDemo')}
            </Button>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 mobile-menu-overlay overflow-y-auto">
          <nav className="container-0g py-6">
            {/* Solutions Section */}
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">{t('nav.solutionsMenu')}</h3>
              <div className="space-y-1">
                {solutionsMenu.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block py-2 px-2 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Products Section */}
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">{t('nav.products')}</h3>
              <div className="space-y-1">
                {productsMenu.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block py-2 px-2 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{item.name}</span>
                    <span className="text-sm text-slate-500 ml-2">— {item.desc}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Company Section */}
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">{t('nav.company')}</h3>
              <div className="space-y-1">
                {companyMenu.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block py-2 px-2 text-base font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Blog Link */}
            <div className="mb-6">
              <Link
                to="/blog"
                className="block py-2 px-2 text-base font-medium text-teal-600 hover:bg-teal-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.blog')}
              </Link>
            </div>

            {/* E2 Insurance Link (Mobile) */}
            <div className="mb-6">
              <Link
                to="/e2-insurance"
                className="flex items-center justify-between py-3 px-4 text-base font-semibold text-white bg-slate-900 rounded-lg shadow-sm"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-e2-insurance-link"
              >
                <span className="flex items-center gap-2">
                  <Droplets className="h-4 w-4 text-teal-400" />
                  E2 Insurance
                </span>
                <span className="px-2 py-0.5 rounded-full bg-teal-400 text-[10px] font-bold text-slate-900">
                  {t('nav.balticHubBadge')}
                </span>
              </Link>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              {/* Mobile Language Switcher */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-3 py-1 text-sm font-medium rounded ${
                      language === lang.code 
                        ? 'bg-teal-600 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`mobile-lang-${lang.code}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              <Button 
                className="w-full btn-primary flex items-center justify-center gap-2"
                onClick={() => {
                  setMobileMenuOpen(false);
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="mobile-schedule-demo"
              >
                <Calendar className="h-4 w-4" />
                {t('nav.scheduleDemo')}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
